import { Component, Inject, OnInit } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { MatTableDataSource } from '@angular/material/table';
import { createId } from '@core/util/file';
import { dollarsToCents } from '@core/util/money';
import {
	DefaultMatterAffidavit,
	DefaultMatterClientWrit,
	MatterClientWrit,
} from '@models/matter';
import {
	Company,
	Execution,
	JudgementInfo,
	Person,
	SavedWrit,
	WritName,
	WritSearchType,
} from '@models/teranet';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { Select } from '@ngxs/store';
import { AffidavitDispatch } from '@state/matter/affidavit';
import { SaleWritState } from '@state/sale_writ';
import { Observable } from 'rxjs';
import { RadioGroupItem } from '../../../../shared/atomic/atoms/radio-group/radio-group.component';

@UntilDestroy()
@Component({
	selector: 'app-import-details',
	templateUrl: 'import-details.component.html',
	standalone: false,
})
export class ImportDetailsComponent implements OnInit {
	public busy: boolean;
	public currentItem: SavedWrit;
	public currentNames: MatTableDataSource<MatterClientWrit>;
	public displayedColumns: string[];
	public searchPeople: RadioGroupItem<SavedWrit>[];
	@Select(SaleWritState.savedItems) public savedItems$: Observable<SavedWrit[]>;
	public selectedWrits: string[];
	public totalAmount: number;
	public trackByNameId = (_, item: WritName): string => item.execution_number;

	constructor(
		private affidavitDispatch: AffidavitDispatch,
		@Inject(MAT_DIALOG_DATA)
		public data: { clientId: string; onlyShowExecutions: boolean },
		public dialogRef: MatDialogRef<ImportDetailsComponent>,
	) {}

	ngOnInit(): void {
		this.busy = false;
		this.currentNames = new MatTableDataSource([]);
		this.displayedColumns = ['selected', 'name', 'id', 'amount', 'plaintiff'];
		this.selectedWrits = [];
		this.totalAmount = 0;
		this.savedItems$.pipe(untilDestroyed(this)).subscribe((items) => {
			this.searchPeople = items.reduce((filtered, item) => {
				if (
					this.data?.onlyShowExecutions &&
					!Object.values(item.writ.names).length
				) {
					return filtered;
				}

				let label = '';
				switch (item.search.type) {
					case WritSearchType.COMPANY:
						label = (item.search.item as Company).name;
						break;
					case WritSearchType.EXECUTION:
						label = (item.search.item as Execution).number;
						break;
					case WritSearchType.PERSON:
						label = `${(item.search.item as Person).firstName} ${(item.search.item as Person).lastName}`;
						break;
				}

				const searchPerson = {
					label,
					value: item,
				};

				filtered.push(searchPerson);

				return filtered;
			}, []);

			this.currentItem = this.searchPeople[0]?.value;
			this.selectItem(this.currentItem);
		});
	}

	cancel(): void {
		this.dialogRef.close();
	}

	import(): void {
		this.currentNames.data
			.filter((writ) => writ.selected)
			.forEach((writ) => {
				const affidavitId = createId();
				this.affidavitDispatch.Create({
					...DefaultMatterAffidavit,
					amount: dollarsToCents(+writ.amount),
					clientId: this.data.clientId,
					costs: dollarsToCents(+writ.costs),
					debtor: writ.name,
					executionNumber: writ.id,
					id: affidavitId,
					plaintiff: writ.plaintiff,
				});
			});
		this.dialogRef.close();
	}

	itemId(index, item: MatterClientWrit): string {
		return item.id;
	}

	selectItem(item: SavedWrit): void {
		const currentNames: WritName[] = [];
		this.selectedWrits = [];

		Object.getOwnPropertyNames(item?.writ?.names).forEach((name) =>
			currentNames.push(item?.writ?.names[name]),
		);

		this.currentNames = new MatTableDataSource(
			currentNames
				.filter((writ) => writ.execution)
				.map(
					(writ) =>
						({
							...DefaultMatterClientWrit,
							amount: writ.execution?.judgment_infos
								? writ.execution.judgment_infos.reduce(
										(prev: number, curr: JudgementInfo) =>
											prev + +curr.Judgmentamount,
										0,
									)
								: 0,
							id: writ.execution_number,
							plaintiff: this.getPlaintiffName(writ),
							name: `${writ.first_name} ${writ.last_name}`,
							selected: true,
						}) as MatterClientWrit,
				),
		);
		this.updatedSelectedWrits();
	}

	updatedSelectedWrits(): void {
		this.selectedWrits = this.currentNames.data
			.filter((writ) => writ.selected)
			.map((writ) => writ.id);
		this.totalAmount = this.currentNames.data
			.filter((writ) => writ.selected)
			.reduce((prev: number, curr: MatterClientWrit) => prev + +curr.amount, 0);
	}

	private getPlaintiffName(writ: WritName): string {
		return writ.execution?.creditors
			.map((creditor) =>
				creditor.AggregateField
					? creditor.AggregateField
					: creditor.CompanyName
						? creditor.CompanyName
						: `${creditor.Firstname} ${creditor.Lastname}`,
			)
			.join(', ');
	}
}
